import { isReference, MedplumClient } from '@medplum/core';
import { Communication, DocumentReference, ServiceRequest } from '@medplum/fhirtypes';
import { useEffect, useState } from 'react';
import { downloadAttachmentBlob, getAttachmentBlob } from '@/services/attachments';
import { HEALTHIE_SYSTEM_URL } from '@bariendo/lib/constants';

async function loadPdfFromDocumentReference(medplum: MedplumClient, docRef: DocumentReference) {
  const attachment = docRef.content?.[0]?.attachment;
  if (!attachment) {
    return undefined;
  }

  return await getAttachmentBlob(medplum, attachment);
}

async function loadHealthieLabOrderPdf(medplum: MedplumClient, serviceRequest: ServiceRequest) {
  const isHealthieLabOrder = serviceRequest.identifier?.some((id) => id.system === HEALTHIE_SYSTEM_URL) ?? false;
  if (!isHealthieLabOrder) {
    return undefined;
  }

  const reasonDocRef = serviceRequest.reasonReference?.find((ref) => isReference(ref, 'DocumentReference'));
  if (!reasonDocRef) {
    return undefined;
  }

  const docRef = (await medplum.readReference(reasonDocRef)) as DocumentReference;
  return await loadPdfFromDocumentReference(medplum, docRef);
}

async function loadHealthGorillaLabOrderPdf(medplum: MedplumClient, parentServiceRequest: ServiceRequest) {
  const requisitionDocRef = parentServiceRequest.supportingInfo?.find((info) =>
    info.display?.includes('LabOrderRequisition')
  );
  if (!requisitionDocRef) {
    return undefined;
  }

  const docRef = (await medplum.readReference(requisitionDocRef)) as DocumentReference;
  return await loadPdfFromDocumentReference(medplum, docRef);
}

export default function useDownloadLabOrder(medplum: MedplumClient, labOrder: Communication) {
  const [labOrderPdf, setLabOrderPdf] = useState({
    url: '',
    filename: '',
  });

  useEffect(() => {
    const getLabOrderPdf = async () => {
      const serviceRequestReference = labOrder.basedOn?.find((ref) => isReference(ref, 'ServiceRequest'));
      if (!serviceRequestReference) {
        return;
      }

      try {
        const serviceRequestResource = (await medplum.readReference(serviceRequestReference)) as ServiceRequest;
        const healthieBlob = await loadHealthieLabOrderPdf(medplum, serviceRequestResource);
        if (healthieBlob) {
          setLabOrderPdf({ url: healthieBlob.url, filename: healthieBlob.filename });
          return;
        }

        const parentRef = serviceRequestResource.basedOn?.find((ref) => isReference(ref, 'ServiceRequest'));
        if (!parentRef) {
          return;
        }

        const parentServiceRequestResource = (await medplum.readReference(parentRef)) as ServiceRequest;
        const healthGorillaBlob = await loadHealthGorillaLabOrderPdf(medplum, parentServiceRequestResource);
        if (healthGorillaBlob) {
          setLabOrderPdf({ url: healthGorillaBlob.url, filename: healthGorillaBlob.filename });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[LabOrderTimelineItem] Error getting lab order PDF:', error);
      }
    };

    getLabOrderPdf();
  }, [medplum, labOrder]);

  const handleDownloadLabOrderPdf = () => {
    if (labOrderPdf.url) {
      downloadAttachmentBlob(labOrderPdf.url, labOrderPdf.filename);
    }
  };

  return {
    labOrderPdf,
    handleDownloadLabOrderPdf,
  };
}
